import React from 'react';
import { ModuleDefinition } from '../../types';
import { playClick } from '../../utils/audioUtils';
type ModuleCardProps = {
  module: ModuleDefinition;
  onClick: (id: string) => void;
};
export function ModuleCard({
  module,
  onClick
}: ModuleCardProps) {
  const handleClick = () => {
    playClick();
    onClick(module.id);
  };
  const renderIllustration = () => {
    switch (module.id) {
      case 'scale':
        // Piano with colorful notes - cleaner design without animal emojis
        return <div className="absolute inset-0 flex flex-col" data-id="element-274">
            {/* Colorful falling notes - larger, harmonious warm-cool gradient */}
            <div className="flex-1 relative overflow-hidden" data-id="element-275">
              {/* Large decorative notes */}
              <div className="absolute top-4 left-6 w-8 h-8 bg-[#FF6B6B] rounded-lg transform rotate-12 shadow-md" data-id="element-276" />
              <div className="absolute top-3 left-[45%] w-7 h-7 bg-[#FFB347] rounded-lg transform -rotate-6 shadow-md" data-id="element-277" />
              <div className="absolute top-5 right-6 w-8 h-8 bg-[#98D8AA] rounded-lg transform rotate-45 shadow-md" data-id="element-278" />
              <div className="absolute top-14 left-10 w-6 h-6 bg-[#87CEEB] rounded-lg transform rotate-20 shadow-sm" data-id="element-279" />
              <div className="absolute top-12 right-12 w-7 h-7 bg-[#DDA0DD] rounded-lg transform -rotate-15 shadow-sm" data-id="element-280" />
              <div className="absolute top-20 left-[40%] w-6 h-6 bg-[#FFEB3B] rounded-lg shadow-sm" data-id="element-281" />

              {/* Music note symbols */}
              <div className="absolute top-8 left-20 text-2xl text-[#FF6B6B]" data-id="element-282">
                ♪
              </div>
              <div className="absolute top-16 right-8 text-xl text-[#98D8AA]" data-id="element-283">
                ♫
              </div>
            </div>

            {/* Piano keys at bottom - taller */}
            <div className="h-28 flex items-end px-3 pb-10" data-id="element-284">
              <div className="flex-1 flex gap-0.5 h-20 relative" data-id="element-285">
                {/* White keys with colors */}
                {['#FF6B6B', '#FFB347', '#FFEB3B', '#98D8AA', '#87CEEB', '#DDA0DD', '#FFB6C1'].map((color, i) => <div key={i} className="flex-1 rounded-b-lg shadow-md relative" style={{
                backgroundColor: color
              }} data-id="element-286">
                    {/* Key shine effect */}
                    <div className="absolute top-0 left-0 right-0 h-2 bg-white/30 rounded-t-sm" data-id="element-287" />
                  </div>)}
              </div>
            </div>
          </div>;
      case 'rhythm':
        // Colorful grid pattern - harmonious color scheme
        return <div className="absolute inset-0 p-4 pb-12" data-id="element-288">
            <div className="w-full h-full grid grid-cols-8 grid-rows-6 gap-1" data-id="element-289">
              {Array.from({
              length: 48
            }).map((_, i) => {
              // Harmonious color palette - warm to cool gradient by row
              const rowColors = [['#FF8A80', '#FF8A80', '#FF8A80', '#FF8A80', '#FF8A80', '#FF8A80', '#FF8A80', '#FF8A80'], ['#FFD180', '#FFD180', '#FFD180', '#FFD180', '#FFD180', '#FFD180', '#FFD180', '#FFD180'], ['#FFE57F', '#FFE57F', '#FFE57F', '#FFE57F', '#FFE57F', '#FFE57F', '#FFE57F', '#FFE57F'], ['#B9F6CA', '#B9F6CA', '#B9F6CA', '#B9F6CA', '#B9F6CA', '#B9F6CA', '#B9F6CA', '#B9F6CA'], ['#84FFFF', '#84FFFF', '#84FFFF', '#84FFFF', '#84FFFF', '#84FFFF', '#84FFFF', '#84FFFF'], ['#B388FF', '#B388FF', '#B388FF', '#B388FF', '#B388FF', '#B388FF', '#B388FF', '#B388FF'] // Row 6 - lavender
              ];
              const row = Math.floor(i / 8);
              const col = i % 8;
              // Active cells form a diagonal melody pattern
              const isActive = [0, 9, 18, 27, 28, 21, 14, 7, 3, 12, 19, 26, 33, 42, 5, 13, 22, 30, 38, 47 // Third melody
              ].includes(i);
              return <div key={i} className="rounded-sm transition-all" style={{
                backgroundColor: isActive ? rowColors[row][col] : '#F0F0F0'
              }} data-id="element-290" />;
            })}
            </div>
          </div>;
      case 'body':
        // Cute bear drummer with rhythm elements
        return <div className="absolute inset-0 pb-8" data-id="element-291">
            {/* Background rhythm elements */}
            <div className="absolute top-3 left-3 flex gap-1" data-id="element-292">
              <div className="w-3 h-3 bg-[#FF7043] rounded-full" data-id="element-293" />
              <div className="w-3 h-6 bg-[#FF7043] rounded-full" data-id="element-294" />
              <div className="w-3 h-4 bg-[#FF7043] rounded-full" data-id="element-295" />
            </div>
            <div className="absolute top-3 right-3 flex gap-1" data-id="element-296">
              <div className="w-3 h-4 bg-[#FFB74D] rounded-full" data-id="element-297" />
              <div className="w-3 h-6 bg-[#FFB74D] rounded-full" data-id="element-298" />
              <div className="w-3 h-3 bg-[#FFB74D] rounded-full" data-id="element-299" />
            </div>

            {/* Music notes floating */}
            <div className="absolute top-6 left-10 text-xl text-[#FF7043]" data-id="element-300">
              ♪
            </div>
            <div className="absolute top-8 right-8 text-lg text-[#FFB74D]" data-id="element-301">
              ♫
            </div>
            <div className="absolute top-14 left-6 text-sm text-[#8D6E63]" data-id="element-302">
              ♩
            </div>
            <div className="absolute top-12 right-12 text-xl text-[#FF5722]" data-id="element-303">
              ♬
            </div>

            {/* Cute bear centered */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-304">
              <div className="relative mt-2" data-id="element-305">
                {/* Cute bear body */}
                <div className="w-24 h-20 bg-[#8D6E63] rounded-[50%] relative" data-id="element-306">
                  {/* Ears */}
                  <div className="absolute -top-2 left-1 w-7 h-7 bg-[#8D6E63] rounded-full" data-id="element-307">
                    <div className="absolute top-1.5 left-1.5 w-4 h-4 bg-[#A1887F] rounded-full" data-id="element-308" />
                  </div>
                  <div className="absolute -top-2 right-1 w-7 h-7 bg-[#8D6E63] rounded-full" data-id="element-309">
                    <div className="absolute top-1.5 left-1.5 w-4 h-4 bg-[#A1887F] rounded-full" data-id="element-310" />
                  </div>
                  {/* Face */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-18 h-14" data-id="element-311">
                    {/* Eyes */}
                    <div className="absolute top-2 left-2 w-3.5 h-3.5 bg-black rounded-full" data-id="element-312">
                      <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full" data-id="element-313" />
                    </div>
                    <div className="absolute top-2 right-2 w-3.5 h-3.5 bg-black rounded-full" data-id="element-314">
                      <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full" data-id="element-315" />
                    </div>
                    {/* Blush */}
                    <div className="absolute top-5 -left-1 w-3 h-2 bg-[#FFAB91] rounded-full opacity-60" data-id="element-316" />
                    <div className="absolute top-5 -right-1 w-3 h-2 bg-[#FFAB91] rounded-full opacity-60" data-id="element-317" />
                    {/* Snout */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-9 h-6 bg-[#D7CCC8] rounded-full" data-id="element-318">
                      <div className="absolute top-1 left-1/2 -translate-x-1/2 w-2.5 h-1.5 bg-[#5D4037] rounded-full" data-id="element-319" />
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-3 h-0.5 border-b border-[#5D4037]" data-id="element-320" />
                    </div>
                  </div>
                </div>
                {/* Drum */}
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2" data-id="element-321">
                  <div className="w-20 h-8 bg-[#FF7043] rounded-lg relative overflow-hidden shadow-md" data-id="element-322">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#FF5722]" data-id="element-323" />
                    <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-[#E64A19]" data-id="element-324" />
                    <div className="absolute top-1.5 left-2 right-2 bottom-1.5 flex justify-around" data-id="element-325">
                      <div className="w-0.5 h-full bg-[#FFCCBC]" data-id="element-326" />
                      <div className="w-0.5 h-full bg-[#FFCCBC]" data-id="element-327" />
                      <div className="w-0.5 h-full bg-[#FFCCBC]" data-id="element-328" />
                    </div>
                  </div>
                  {/* Drum sticks */}
                  <div className="absolute -left-4 -top-1 w-8 h-1.5 bg-[#FFCC80] rounded-full transform -rotate-45 origin-right shadow-sm" data-id="element-329" />
                  <div className="absolute -right-4 -top-1 w-8 h-1.5 bg-[#FFCC80] rounded-full transform rotate-45 origin-left shadow-sm" data-id="element-330" />
                </div>
              </div>
            </div>

            {/* Bottom rhythm dots */}
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-331">
              <div className="w-2 h-2 bg-[#FF7043] rounded-full" data-id="element-332" />
              <div className="w-2 h-2 bg-[#FFB74D] rounded-full" data-id="element-333" />
              <div className="w-2 h-2 bg-[#FF7043] rounded-full" data-id="element-334" />
              <div className="w-2 h-2 bg-[#FFB74D] rounded-full" data-id="element-335" />
            </div>
          </div>;
      case 'modes':
        // Colorful wheel - Chord style
        return <div className="absolute inset-0 flex items-center justify-center pb-8" data-id="element-336">
            <div className="relative w-36 h-36" data-id="element-337">
              {['#FF6B6B', '#FFB347', '#FFEB3B', '#98D8AA', '#87CEEB', '#DDA0DD', '#FFB6C1', '#B19CD9'].map((color, i) => <div key={i} className="absolute w-full h-full" style={{
              background: `conic-gradient(from ${i * 45}deg, ${color} 0deg, ${color} 45deg, transparent 45deg)`
            }} data-id="element-338" />)}
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-339">
                <div className="w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center" data-id="element-340">
                  <div className="w-0 h-0 border-l-[10px] border-l-gray-700 border-y-[7px] border-y-transparent ml-1" data-id="element-341" />
                </div>
              </div>
              <div className="absolute top-1 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-600" data-id="element-342">
                宫
              </div>
              <div className="absolute top-6 right-2 text-xs font-bold text-gray-600" data-id="element-343">
                商
              </div>
              <div className="absolute bottom-6 right-2 text-xs font-bold text-gray-600" data-id="element-344">
                角
              </div>
              <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-600" data-id="element-345">
                徵
              </div>
              <div className="absolute bottom-6 left-2 text-xs font-bold text-gray-600" data-id="element-346">
                羽
              </div>
            </div>
          </div>;
      case 'staff':
        // Staff notation with cute character
        return <div className="absolute inset-0 flex items-center justify-center p-6 pb-12" data-id="element-347">
            <div className="w-full" data-id="element-348">
              <div className="relative h-20" data-id="element-349">
                {[0, 1, 2, 3, 4].map(i => <div key={i} className="absolute w-full h-0.5 bg-gray-300" style={{
                top: `${i * 20 + 10}%`
              }} data-id="element-350" />)}
                <div className="absolute left-6 top-[30%] w-5 h-5 bg-[#4CAF50] rounded-full" data-id="element-351" />
                <div className="absolute left-16 top-[50%] w-5 h-5 bg-[#2196F3] rounded-full" data-id="element-352" />
                <div className="absolute left-28 top-[10%] w-5 h-5 bg-[#FF9800] rounded-full" data-id="element-353" />
                <div className="absolute right-6 top-[70%] w-5 h-5 bg-[#E91E63] rounded-full" data-id="element-354" />
              </div>
              <div className="absolute bottom-12 right-4" data-id="element-355">
                <div className="w-10 h-10 bg-[#4DD0E1] rounded-full relative" data-id="element-356">
                  <div className="absolute top-2 left-1.5 w-2 h-2 bg-black rounded-full" data-id="element-357" />
                  <div className="absolute top-2 right-1.5 w-2 h-2 bg-black rounded-full" data-id="element-358" />
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-3 h-1.5 border-b-2 border-black rounded-full" data-id="element-359" />
                </div>
              </div>
            </div>
          </div>;
      case 'sing':
        // Microphone with cute bird characters
        return <div className="absolute inset-0 pb-8" data-id="element-360">
            {/* Background music notes */}
            <div className="absolute top-4 left-4 text-2xl text-[#00BCD4] opacity-40" data-id="element-361">
              ♪
            </div>
            <div className="absolute top-6 right-6 text-xl text-[#4DD0E1] opacity-50" data-id="element-362">
              ♫
            </div>
            <div className="absolute top-12 left-8 text-lg text-[#26C6DA] opacity-30" data-id="element-363">
              ♩
            </div>
            <div className="absolute top-10 right-10 text-2xl text-[#00ACC1] opacity-40" data-id="element-364">
              ♬
            </div>

            {/* Sound wave circles */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-[#00BCD4] rounded-full opacity-10" data-id="element-365" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 border-2 border-[#00BCD4] rounded-full opacity-5" data-id="element-366" />

            {/* Main microphone */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-367">
              <div className="relative" data-id="element-368">
                {/* Sound waves left */}
                <div className="absolute -left-8 top-1/2 -translate-y-1/2 flex flex-col gap-1" data-id="element-369">
                  <div className="w-4 h-1 bg-[#00BCD4] rounded-full opacity-60" data-id="element-370" />
                  <div className="w-6 h-1 bg-[#00BCD4] rounded-full opacity-40" data-id="element-371" />
                  <div className="w-4 h-1 bg-[#00BCD4] rounded-full opacity-60" data-id="element-372" />
                </div>
                {/* Sound waves right */}
                <div className="absolute -right-8 top-1/2 -translate-y-1/2 flex flex-col gap-1" data-id="element-373">
                  <div className="w-4 h-1 bg-[#00BCD4] rounded-full opacity-60" data-id="element-374" />
                  <div className="w-6 h-1 bg-[#00BCD4] rounded-full opacity-40" data-id="element-375" />
                  <div className="w-4 h-1 bg-[#00BCD4] rounded-full opacity-60" data-id="element-376" />
                </div>

                {/* Microphone */}
                <div className="w-14 h-20 bg-gradient-to-b from-[#37474F] to-[#263238] rounded-t-full relative shadow-lg" data-id="element-377">
                  <div className="absolute inset-2 bg-gradient-to-b from-[#546E7A] to-[#455A64] rounded-t-full" data-id="element-378">
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-6 space-y-1" data-id="element-379">
                      <div className="h-px bg-[#37474F]" data-id="element-380" />
                      <div className="h-px bg-[#37474F]" data-id="element-381" />
                      <div className="h-px bg-[#37474F]" data-id="element-382" />
                    </div>
                  </div>
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-3 h-8 bg-gradient-to-b from-[#37474F] to-[#263238]" data-id="element-383" />
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-10 h-2.5 bg-[#263238] rounded-full shadow-md" data-id="element-384" />
                </div>
              </div>
            </div>

            {/* Cute singing bird - right */}
            <div className="absolute bottom-12 right-4" data-id="element-385">
              <div className="relative" data-id="element-386">
                {/* Bird body */}
                <div className="w-10 h-9 bg-[#FFEB3B] rounded-full relative" data-id="element-387">
                  {/* Wing */}
                  <div className="absolute top-2 -left-1 w-4 h-5 bg-[#FFC107] rounded-full transform -rotate-20" data-id="element-388" />
                  {/* Eye */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-black rounded-full" data-id="element-389">
                    <div className="absolute top-0 left-0 w-1 h-1 bg-white rounded-full" data-id="element-390" />
                  </div>
                  {/* Beak - open singing */}
                  <div className="absolute top-3 -right-2 w-0 h-0 border-t-[4px] border-t-[#FF9800] border-l-[6px] border-l-transparent border-b-[4px] border-b-[#FF9800]" data-id="element-391" />
                  {/* Blush */}
                  <div className="absolute bottom-2 right-1 w-2 h-1 bg-[#FFAB91] rounded-full opacity-50" data-id="element-392" />
                </div>
                {/* Feet */}
                <div className="absolute -bottom-1 left-3 w-1 h-2 bg-[#FF9800]" data-id="element-393" />
                <div className="absolute -bottom-1 left-5 w-1 h-2 bg-[#FF9800]" data-id="element-394" />
              </div>
            </div>

            {/* Cute singing bird - left */}
            <div className="absolute bottom-14 left-4" data-id="element-395">
              <div className="relative" data-id="element-396">
                {/* Bird body */}
                <div className="w-8 h-7 bg-[#81D4FA] rounded-full relative" data-id="element-397">
                  {/* Wing */}
                  <div className="absolute top-1.5 -right-0.5 w-3 h-4 bg-[#4FC3F7] rounded-full transform rotate-20" data-id="element-398" />
                  {/* Eye */}
                  <div className="absolute top-1.5 left-1.5 w-1.5 h-1.5 bg-black rounded-full" data-id="element-399">
                    <div className="absolute top-0 left-0 w-0.5 h-0.5 bg-white rounded-full" data-id="element-400" />
                  </div>
                  {/* Beak */}
                  <div className="absolute top-2 -left-1.5 w-0 h-0 border-t-[3px] border-t-[#FF9800] border-r-[5px] border-r-transparent border-b-[3px] border-b-[#FF9800]" data-id="element-401" />
                </div>
                {/* Feet */}
                <div className="absolute -bottom-1 left-2 w-0.5 h-1.5 bg-[#FF9800]" data-id="element-402" />
                <div className="absolute -bottom-1 left-4 w-0.5 h-1.5 bg-[#FF9800]" data-id="element-403" />
              </div>
            </div>
          </div>;
      case 'listen':
        // Sound waves visualization
        return <div className="absolute inset-0 flex flex-col items-center justify-center pb-8" data-id="element-404">
            <div className="flex items-end gap-1 h-20 mb-2" data-id="element-405">
              {[40, 60, 80, 100, 80, 60, 40, 55, 75, 95, 75, 55, 35].map((height, i) => <div key={i} className="w-2.5 rounded-full transition-all" style={{
              height: `${height}%`,
              backgroundColor: ['#FF6B6B', '#FFB347', '#FFEB3B', '#98D8AA', '#87CEEB', '#DDA0DD', '#FF6B6B', '#FFB347', '#FFEB3B', '#98D8AA', '#87CEEB', '#DDA0DD', '#FF6B6B'][i]
            }} data-id="element-406" />)}
            </div>
            <div className="absolute bottom-12 right-4" data-id="element-407">
              <div className="w-10 h-10 bg-[#FFB74D] rounded-full relative" data-id="element-408">
                <div className="absolute top-2 left-2 w-1.5 h-1.5 bg-black rounded-full" data-id="element-409" />
                <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-black rounded-full" data-id="element-410" />
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-2.5 h-1 bg-black rounded-full" data-id="element-411" />
                <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-2.5 h-3 bg-[#FFB74D] rounded-full" data-id="element-412" />
              </div>
            </div>
          </div>;
      case 'melody':
        // Melody drawing
        return <div className="absolute inset-0 flex items-center justify-center p-4 pb-12" data-id="element-413">
            <div className="w-full h-full relative" data-id="element-414">
              <div className="absolute inset-0 opacity-20" data-id="element-415">
                {[0, 1, 2, 3, 4].map(i => <div key={i} className="absolute w-full h-px bg-gray-400" style={{
                top: `${20 + i * 15}%`
              }} data-id="element-416" />)}
              </div>
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 150" data-id="element-417">
                <path d="M 20 100 Q 50 40 80 80 T 140 60 T 180 90" fill="none" stroke="url(#melodyGradient)" strokeWidth="6" strokeLinecap="round" data-id="element-418" />
                <defs data-id="element-419">
                  <linearGradient id="melodyGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-420">
                    <stop offset="0%" stopColor="#FF6B6B" data-id="element-421" />
                    <stop offset="25%" stopColor="#FFB347" data-id="element-422" />
                    <stop offset="50%" stopColor="#FFEB3B" data-id="element-423" />
                    <stop offset="75%" stopColor="#98D8AA" data-id="element-424" />
                    <stop offset="100%" stopColor="#87CEEB" data-id="element-425" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute right-4 top-6 transform rotate-45" data-id="element-426">
                <div className="w-3 h-12 bg-[#FFD54F] rounded-t-sm relative" data-id="element-427">
                  <div className="absolute bottom-0 left-0 w-3 h-3 bg-[#FF7043]" style={{
                  clipPath: 'polygon(50% 100%, 0 0, 100% 0)'
                }} data-id="element-428" />
                  <div className="absolute top-0 left-0 w-3 h-2 bg-[#E91E63]" data-id="element-429" />
                </div>
              </div>
              <div className="absolute top-3 left-6 text-xl" data-id="element-430">♪</div>
              <div className="absolute top-8 right-12 text-lg" data-id="element-431">♫</div>
            </div>
          </div>;
      case 'tempo':
        // Metronome
        return <div className="absolute inset-0 flex items-center justify-center pb-8" data-id="element-432">
            <div className="relative" data-id="element-433">
              <div className="w-24 h-32 bg-[#8D6E63] rounded-t-lg relative" style={{
              clipPath: 'polygon(10% 100%, 90% 100%, 100% 0, 0 0)'
            }} data-id="element-434">
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-16 bg-[#EFEBE9] rounded-lg" data-id="element-435">
                  <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-px h-2 bg-gray-400" data-id="element-436" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-12 bg-[#795548] origin-bottom transform -rotate-12" data-id="element-437">
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#FF5722] rounded-full" data-id="element-438" />
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex justify-between" data-id="element-439">
                  <div className="w-1.5 h-1.5 bg-[#FFB74D] rounded-full" data-id="element-440" />
                  <div className="w-1.5 h-1.5 bg-[#FFB74D] rounded-full" data-id="element-441" />
                </div>
              </div>
              <div className="absolute -left-6 top-1/2 -translate-y-1/2 flex flex-col gap-0.5" data-id="element-442">
                <div className="text-[10px] font-bold text-gray-500" data-id="element-443">快</div>
                <div className="text-[10px] font-bold text-gray-400" data-id="element-444">中</div>
                <div className="text-[10px] font-bold text-gray-500" data-id="element-445">慢</div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <button onClick={handleClick} className="relative overflow-hidden rounded-2xl aspect-square group focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-blue-400 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl bg-white shadow-md" data-id="element-446">
      {/* Illustration */}
      {renderIllustration()}

      {/* Title at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-white via-white/90 to-transparent" data-id="element-447">
        <h3 className="text-base font-bold text-gray-800" data-id="element-448">{module.title}</h3>
      </div>
    </button>;
}